import React, { useEffect, useMemo, useState, Suspense } from 'react';
import Routes from './_routes';
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  useUser,
  useOrganization,
  useClerk,
  RedirectToSignIn,
} from '@clerk/clerk-react';
import { BrowserRouter, useLocation, Route, Switch, Redirect } from 'react-router-dom';

import { loadStripe } from '@stripe/stripe-js';
import MobileNotSupported from 'modules/App/components/MobileNotSupported';
import { initRum, updateUserInfoForCrashReporting } from './_rum';
import { initActionLogger, logCustomAction, logLifecycleEvent } from './_actionLogger';
import { initMixpanel, trackPageView } from 'helpers/mixpanel';
import ErrorTestingTools from 'modules/App/components/ErrorTestingTools';
import ErrorReportingProvider from './components/ErrorReportingProvider';

import { FreemiumProvider, useFreemium } from 'modules/Common/freemium/FreemiumContext';
import CgUpdateBanner from 'modules/Common/freemium/CgUpdateBanner';
import CgArchivePopover from 'modules/Common/freemium/CgArchivePopover';
import { BackgroundJobsProvider } from './backgroundJobs/BackgroundJobsContext';
import BackgroundJobsBanner from './backgroundJobs/BackgroundJobsBanner';
import { OrganizationType } from 'modules/Common/utils/Utility';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from '../../queryClient';
import { resetUploadHubSession } from 'modules/UploadHub/stores/useUploadHubStore';
import http from 'helpers/requests';
import MagicLink from '../Auth/MagicLink';
import { AppLayout } from './components/AppLayout';
import AssignImportsOfferHost from 'modules/Common/ui/AssignImportsOfferHost';
import { OrgBoundary } from './OrgBoundary';
import SignInPage from 'modules/Auth/pages/SignInPage';
import SignUpPage from 'modules/Auth/pages/SignUpPage';
import {
  buildSignInUrlWithRedirect,
  hasPendingClerkHandshake,
  isAuthFlowPath,
} from '../Auth/redirectUrl';
import CgPageLoader from 'modules/Common/layout/CgPageLoader';
import RouteChunkFallback from './components/RouteChunkFallback';
import LocaleSwitchOverlay from 'modules/App/components/LocaleSwitchOverlay';
import { CgSpotlightHost } from 'modules/Common/ui/CgSpotlight';
import { buildClerkLocalization } from 'modules/Auth/config/clerkLocalization';
import { loadClerkLocalization } from 'i18n';
import { useLocaleStore } from 'stores/useLocaleStore';
import { useBranding } from './hooks/useBranding';
import { useCloseModalsOnNavigate } from './hooks/useCloseModalsOnNavigate';
import EmbedRoutes from '../Embed/embedRoutes';
import { inPlanFrame } from './utils/planFrame';
import PlanFrameGuard from './components/PlanFrameGuard';
import { isEmbedPath, isPublicDiagramPath, isUkCalculatorEmbedPath } from '../Embed/useEmbedConfig';
import PublicDiagramRoutes from 'modules/Diagrams/public/publicDiagramRoutes';
import AuditRoutes from 'modules/Audit/pages/auditRoutes';
import { isAuditPath } from 'modules/Audit/utils/isAuditPath';
import {
  isVerifierBlockedPath,
  isVerifierWorkspacePath,
} from 'modules/Verifier/utils/verifierPaths';
import VerifierBlockedPage from 'modules/Verifier/pages/VerifierBlockedPage';
import PublicCatalogRoutes from '../PublicCatalog/publicCatalogRoutes';
import { isPublicCatalogPath } from '../PublicCatalog/hooks/useFetchPublicCatalogShare';
import SupplierInvitationRoutes from '../FreeSupplier/SupplierInvitationRoutes';
import SupplierPublicRoutes from '../FreeSupplier/SupplierPublicRoutes';
import {
  isSupplierHomePath,
  isSupplierInvitationContextLocation,
  isSupplierInvitationPath,
  isSupplierPublicPath,
} from '../FreeSupplier/routes';

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
export const stripePubKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

// cbam.faradai.ai is the single exception that shares the CG Clerk instance and
// is served by the CG build as a Clerk satellite domain. All other white-label
// builds have their own Clerk instance and must never enter this path.
const SATELLITE_HOST = 'cbam.faradai.ai';
const PRIMARY_ORIGIN = 'https://app.carbonglance.com';
const IS_CG_MAIN_INSTANCE = (process.env.REACT_APP_WHITELABEL || 'carbonglance') === 'carbonglance';

const isOnSatelliteHost = () =>
  typeof window !== 'undefined' && window.location.hostname === SATELLITE_HOST;

const SATELLITE_ORIGIN = `https://${SATELLITE_HOST}`;

const getClerkSatelliteProps = () => {
  if (!IS_CG_MAIN_INSTANCE) return {};
  // On the CG primary host, whitelist the satellite so Clerk allows post-auth
  // redirects back to cbam.faradai.ai. On the satellite host itself, flip into
  // satellite mode and whitelist the primary for the outbound auth redirect.
  if (!isOnSatelliteHost()) {
    return { allowedRedirectOrigins: [SATELLITE_ORIGIN] };
  }
  return {
    isSatellite: true,
    domain: SATELLITE_HOST,
    allowedRedirectOrigins: [PRIMARY_ORIGIN],
    signInUrl: `${PRIMARY_ORIGIN}/sign-in`,
    signUpUrl: `${PRIMARY_ORIGIN}/sign-up`,
  };
};

const IN_PLAN_FRAME = inPlanFrame();

const ContentWrapper = ({ children }) => {
  const freemiumContext = useFreemium();
  const hasExceededLimit = freemiumContext?.hasExceededLimit || false;
  const location = useLocation();
  const { user } = useUser();
  const { organization } = useOrganization();
  // Don't fingerprint logged-in CG users in Mixpanel / crash reporting when
  // they happen to encounter the embed on a third-party site. The iframe runs
  // in the app.carbonglance.com origin, so if the browser passes through the
  // Clerk session cookie (older browsers / permissive third-party-cookie
  // settings) we'd otherwise learn which partner pages Alice is visiting —
  // a privacy leak Alice didn't consent to.
  const isEmbed = isEmbedPath(location.pathname);
  // The same leak applies to the public supplier widget — a signed-in CG user
  // viewing a third-party page that embeds /public/cbam-widget/:slug must not have
  // their Clerk identity tied to which supplier's public page they visited.
  const isPublicSupplierSurface =
    isPublicCatalogPath(location.pathname) || isSupplierPublicPath(location.pathname);
  const isSensitiveSupplierInvitation = isSupplierInvitationContextLocation(
    location.pathname,
    location.search,
  );

  // Apply branding CSS variables based on URL params (or defaults)
  useBranding();

  // A page's modals do not survive leaving that page — the flags live in
  // module-global stores and would otherwise reopen on the next visit.
  useCloseModalsOnNavigate();

  // Routes that should NOT have navbar/sidebar (outside the main app)
  //
  // `/trial-expired` is deliberately NOT here. A user whose trial has lapsed
  // still needs the sidebar's workspace switcher: they may belong to another
  // org with a live plan, and stripping the chrome left them with no way to
  // reach it short of editing the URL. The nav links stay clickable and simply
  // bounce back — the interceptor in helpers/requests.js returns them here and
  // explicitly guards against a redirect loop.
  const outsideRoutes = [
    '/',
    '/init',
    '/payment',
    '/pricing',
    '/upgrade',
    '/sign-in',
    '/sign-up',
    '/magic-link',
    '/onboarding/type-selection',
    '/onboarding/wizard',
  ];

  // Check if current route is an outside route
  const isOutsideRoute =
    outsideRoutes.includes(location.pathname) ||
    isSupplierInvitationPath(location.pathname) ||
    // The free-tier supplier journey (/supplier/*) renders inside its own slim
    // LiteShell (see FreeSupplier/app/routes.jsx), so it must bypass AppLayout.
    isSupplierHomePath(location.pathname) ||
    isEmbedPath(location.pathname) ||
    isPublicDiagramPath(location.pathname) ||
    isPublicCatalogPath(location.pathname) ||
    // The external auditor's surface renders in its own AuditShell — no
    // sidebar, no org switcher, nothing to switch to.
    isAuditPath(location.pathname) ||
    isVerifierBlockedPath(location.pathname) ||
    isVerifierWorkspacePath(location.pathname) ||
    isSupplierPublicPath(location.pathname);

  useEffect(() => {
    logLifecycleEvent('ContentWrapper', 'mounted');
    return () => logLifecycleEvent('ContentWrapper', 'unmounted');
  }, []);

  // Identify user in Mixpanel when user data is available.
  // Skipped on embed routes and the public supplier routes — see isEmbed
  // comment above.
  useEffect(() => {
    if (IN_PLAN_FRAME || isEmbed || isPublicSupplierSurface || isSensitiveSupplierInvitation)
      return;
    if (user && organization) {
      import('helpers/mixpanel').then(({ identifyUser }) => {
        identifyUser(user, organization);
      });
    }
  }, [isEmbed, isPublicSupplierSurface, isSensitiveSupplierInvitation, user, organization]);

  // Update user info for crash reporting.
  // Skipped on embed routes and the public supplier routes — see isEmbed
  // comment above.
  useEffect(() => {
    if (IN_PLAN_FRAME || isEmbed || isPublicSupplierSurface || isSensitiveSupplierInvitation)
      return;
    if (user) {
      updateUserInfoForCrashReporting({
        userId: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName,
        organizationId: organization?.id,
      });
    }
  }, [isEmbed, isPublicSupplierSurface, isSensitiveSupplierInvitation, user, organization]);

  return (
    <div className={`relative transition-all duration-300 ${hasExceededLimit ? 'mt-10' : ''}`}>
      {IN_PLAN_FRAME ? <PlanFrameGuard /> : null}
      {isOutsideRoute || IN_PLAN_FRAME ? (
        // Outside routes: render without AppLayout (no navbar/sidebar)
        <Suspense fallback={<RouteChunkFallback />}>{children}</Suspense>
      ) : (
        // Main app routes: render with AppLayout (navbar/sidebar).
        //
        // The Suspense boundary sits INSIDE AppLayout deliberately. AppLayout is
        // the persistent sidebar/header that must not re-render on navigation
        // (see CLAUDE.md, "Layout Architecture"); a boundary ABOVE it would
        // unmount the entire chrome every time a lazy route chunk loads, so the
        // sidebar would blink out and remount on each page change. Below it,
        // only the content area shows the loader.
        //
        // ContentWrapper is the one choke point every route surface passes
        // through — supplier invitations, embeds, public diagram/catalog, the
        // auth switch and the authenticated <Routes/> — so putting the boundary
        // here covers all of them. A boundary at any single <Routes/> call site
        // would miss the rest.
        <AppLayout>
          <Suspense fallback={<RouteChunkFallback />}>{children}</Suspense>
          <AssignImportsOfferHost />
        </AppLayout>
      )}
    </div>
  );
};

// How long to let clerk-js consume handshake state off the URL before giving up
// and sending the visitor to /sign-in as usual. Bounded deliberately: clerk-js
// strips the params with history.replaceState, which does NOT re-render
// react-router, so on the failure path nothing would ever move us off this
// screen. A stuck loader is not an improvement over a redirect.
const CLERK_HANDSHAKE_GRACE_MS = 5000;

// Signed-out visitors normally bounce straight to /sign-in. But when Clerk has
// just handed us session state in the URL (an SSO/SAML landing), that bounce
// destroys the hand-off — buildSignInUrlWithRedirect collapses the URL to a
// bare path, taking the handshake with it, and the visitor lands back on
// /sign-in with nothing to show for a successful IdP round-trip. Hold the
// redirect while the state is unconsumed, then fall through.
const ClerkHandshakeGate = ({ location }) => {
  const [graceExpired, setGraceExpired] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setGraceExpired(true), CLERK_HANDSHAKE_GRACE_MS);
    return () => clearTimeout(timer);
  }, []);

  if (graceExpired) {
    return <Redirect to={buildSignInUrlWithRedirect(location)} />;
  }

  return (
    <CgPageLoader
      variant="fullscreen"
      ariaLabel="Completing sign-in"
    />
  );
};

// Operator-report renders (full report + the heavy Production Processes
// section) only exist for the org types that can reach the generation route —
// Operator + Consultant. Mount the background-jobs provider/banner/pollers ONLY
// there, never in the Declarant diagram view, so the banner never shows and we
// never poll render-status outside the operator context. Switching orgs
// unmounts it cleanly (and remounts → rehydrates from localStorage on return).
const OPERATOR_REPORT_ORG_TYPES = [OrganizationType.Operator, OrganizationType.Consultant];

const BackgroundJobsScope = ({ children }) => {
  const { organization } = useOrganization();
  const userType = organization?.publicMetadata?.type;
  if (IN_PLAN_FRAME || !OPERATOR_REPORT_ORG_TYPES.includes(userType)) return children;
  // `key` forces a clean remount on org switch (fresh per-org state, pollers
  // torn down) and `orgId` scopes the localStorage persistence — together they
  // keep one org's in-flight renders from leaking into another's banner.
  return (
    <BackgroundJobsProvider
      key={organization?.id}
      orgId={organization?.id}
    >
      <BackgroundJobsBanner />
      {children}
    </BackgroundJobsProvider>
  );
};

/**
 * Inner app content that renders based on auth state.
 * Separated so it can access the Clerk context.
 */
const AppContent = ({ isSatellite }) => {
  const clerk = useClerk();
  const location = useLocation();
  const isSensitiveSupplierInvitation = isSupplierInvitationContextLocation(
    location.pathname,
    location.search,
  );
  const isPublicSupplierSurface =
    isPublicCatalogPath(location.pathname) || isSupplierPublicPath(location.pathname);

  // Invitation tokens are bearer credentials. Do not initialize telemetry on
  // the invite or its auth-return pages: SDKs can capture the current URL at
  // initialization before a component-level effect can redact it. Once the
  // user reaches a token-free route, initialize normally and emit an explicit
  // query-free page view.
  useEffect(() => {
    if (IN_PLAN_FRAME || isSensitiveSupplierInvitation || isPublicSupplierSurface) return;
    initRum();
    initActionLogger();
    initMixpanel();
    trackPageView(location.pathname);
  }, [isSensitiveSupplierInvitation, isPublicSupplierSurface, location.pathname]);

  // Clear our cached backend token AND the react-query cache the instant
  // Clerk's session goes away. Sign-out goes through UserMenu, which calls
  // clerk.signOut() and nothing else — no caller touches localStorage — so
  // without this the previous session's `cg_backend_token` survives sign-out
  // and gets replayed on the next (SSO) sign-in. Registered before the early
  // returns below so the hook order stays stable.
  //
  // The queryClient.clear() makes sign-out a hard cache boundary. Sign-out →
  // sign-in is a pure SPA transition (no reload), so the module-level query
  // cache would otherwise survive into the next session — including empty/401
  // results cached during teardown, when focus-triggered refetches go out with
  // a dead session token. OrgBoundary only clears on a real org→org switch
  // (re-login is a null→org acquisition there), so this listener is the only
  // place the logout boundary can be enforced.
  useEffect(() => {
    if (!clerk || typeof clerk.addListener !== 'function') return undefined;
    const unsubscribe = clerk.addListener(({ session }) => {
      if (!session) {
        http.clearClerkToken();
        queryClient.clear();
        // The Upload Hub keeps its files and review in memory (and the open
        // batch id in this tab), which would otherwise greet the next account.
        resetUploadHubSession();
      }
    });
    return unsubscribe;
  }, [clerk]);

  // Supplier invitation links must preserve their context for signed-in and
  // signed-out visitors. Keep this inside ClerkProvider but outside the auth
  // split and the authenticated app chrome.
  if (isSupplierInvitationPath(location.pathname)) {
    return (
      <ContentWrapper>
        <SupplierInvitationRoutes />
      </ContentWrapper>
    );
  }

  // Partner iframe embed: short-circuit BEFORE the SignedIn/SignedOut gate.
  // The embed authenticates via partner API key (not Clerk), so we must never
  // route through RedirectToSignIn or wrap content in the chrome-heavy
  // ContentWrapper. ClerkProvider is still mounted above so shared components
  // that call useUser/useOrganization don't crash — they'll just return null.
  if (isEmbedPath(location.pathname)) {
    return (
      <ContentWrapper>
        <EmbedRoutes />
      </ContentWrapper>
    );
  }

  // Public diagram links: unauthenticated, password-gated. Short-circuit
  // before Clerk's SignedIn gate for the same reason as embeds above.
  if (isPublicDiagramPath(location.pathname)) {
    return (
      <ContentWrapper>
        <PublicDiagramRoutes />
      </ContentWrapper>
    );
  }

  // External auditor surface. AUTHENTICATED — unlike the public short-circuits
  // above — but rendered outside OrgBoundary/PrivateRoute/PermissionsProvider,
  // because an auditor holds no Clerk organisation by design (ADR-0003). Going
  // through the normal tree would bounce them to /init forever and hang
  // PermissionsProvider's 30s provisioning grace on a user who will never have
  // permissions.
  // A removed verifier who followed a new invite: signed in, usually with no org, so the org tree
  // would send them to onboarding (verifier-firm-foundation phase 2).
  if (isVerifierBlockedPath(location.pathname)) {
    return (
      <ContentWrapper>
        <SignedIn>
          <VerifierBlockedPage />
        </SignedIn>
        <SignedOut>
          <RedirectToSignIn />
        </SignedOut>
      </ContentWrapper>
    );
  }

  if (isAuditPath(location.pathname)) {
    return (
      <ContentWrapper>
        <SignedIn>
          <AuditRoutes />
        </SignedIn>
        <SignedOut>
          {/* Audit links are verifier engagements now (verifier-firm-foundation phase 5): the
              person has an account and signs in with an email code (VF12), then comes back here
              and is redirected to the engagement. */}
          <Redirect to={`/sign-in?redirect_url=${encodeURIComponent(location.pathname)}`} />
        </SignedOut>
      </ContentWrapper>
    );
  }

  // Public CBAM Portal: unauthenticated, password-gated share link keyed by an
  // operator-configurable slug. Same short-circuit pattern as the diagram
  // public links.
  if (isPublicCatalogPath(location.pathname)) {
    return (
      <ContentWrapper>
        <PublicCatalogRoutes />
      </ContentWrapper>
    );
  }

  // Chrome-less CBAM Portal widget: unauthenticated and protected by the same
  // optional password as the full PublicCatalogSharePage.
  if (isSupplierPublicPath(location.pathname)) {
    return (
      <ContentWrapper>
        <SupplierPublicRoutes />
      </ContentWrapper>
    );
  }

  // The auth flows themselves sit OUTSIDE the SignedIn/SignedOut gate, on
  // purpose. Clerk owns /sign-in and /sign-up end to end and keeps its
  // multi-step state on the hash of those same paths (`#/sso-callback`,
  // `#/factor-two`, `#/tasks/setup-mfa`), redirecting out to afterSignInUrl
  // itself once the flow finishes. Gating them on <SignedOut> broke that:
  // clerk-js flips the session to signed-in mid-flow, which unmounts the very
  // <SignIn> card that was about to run the redirect, and the signed-in tree
  // has no /sign-in route — so _routes.jsx' catch-all rendered a 404 at
  // /sign-in# instead. That is what an instance with required MFA hits: the
  // first sign-in comes back as a `pending` session carrying a `setup-mfa`
  // task, and completing the enrolment is exactly the moment the session goes
  // active. Same fix covers MagicLink, which setActive()s and then navigates
  // on a timer.
  //
  // Satellite hosts are excluded: there the point is to bounce to the primary
  // origin's card (<RedirectToSignIn /> below), never to render our own.
  if (!isSatellite && isAuthFlowPath(location.pathname)) {
    return (
      <>
        <ContentWrapper>
          <Switch>
            <Route
              path="/sign-in"
              exact
              component={SignInPage}
            />
            <Route
              path="/sign-up"
              exact
              component={SignUpPage}
            />
            <Route
              path="/magic-link"
              exact
              component={MagicLink}
            />
          </Switch>
        </ContentWrapper>
        {/* Dev/QA-only affordance (it gates itself); the brand preview inside
            it is how whitelabel schemes get eyeballed on the auth screens. */}
        <ErrorTestingTools />
      </>
    );
  }

  return (
    <>
      <SignedIn>
        <ErrorReportingProvider />
        <CgUpdateBanner />
        <CgArchivePopover />
        {/* OrgBoundary mounts <PermissionsProvider> itself, ABOVE its switch
            loader — everything here renders inside it. It used to be wrapped
            here instead, i.e. as a child of the loader branch, which meant the
            provider was unmounted for the whole switch and could only start
            fetching /permissions/me on the frame the loader dropped. Every
            switch therefore revealed the app with permissions still `loading`,
            which every consumer that isn't a route guard reads as DENY. See
            docs/features/org-switch-routing/phase-4-permissions-gate.md. */}
        <OrgBoundary>
          <BackgroundJobsScope>
            <ContentWrapper>
              <Routes />
            </ContentWrapper>
          </BackgroundJobsScope>
        </OrgBoundary>
        <ErrorTestingTools />
      </SignedIn>
      <SignedOut>
        {isSatellite ? (
          <RedirectToSignIn />
        ) : (
          <>
            {/* /sign-in, /sign-up and /magic-link never reach here — the
                short-circuit above renders them for both auth states so Clerk
                can finish its own flow. All that's left is bouncing everything
                else to the sign-in card. */}
            <ContentWrapper>
              <Switch>
                <Route
                  path="/"
                  render={({ location }) =>
                    hasPendingClerkHandshake(location) ? (
                      <ClerkHandshakeGate location={location} />
                    ) : (
                      <Redirect to={buildSignInUrlWithRedirect(location)} />
                    )
                  }
                />
              </Switch>
            </ContentWrapper>
            {/* Same debug affordance as the SignedIn tree — the brand preview
                inside it is how whitelabel schemes get eyeballed on the auth
                screens (dev/QA only; the component gates itself). */}
            <ErrorTestingTools />
          </>
        )}
      </SignedOut>
    </>
  );
};

const ResponsiveAppContent = ({ isSatellite }) => {
  const location = useLocation();
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  // Only the public portal links bypass the mobile block. Authenticated
  // supplier/dashboard routes keep the existing desktop/tablet requirement.
  //
  // The audit surface is exempt too: an external auditor arrives from a
  // magic-link EMAIL, which is the one place a phone is the likely first client,
  // on a link addressed to them personally. Being exempt is not the same as being
  // responsive — the plan works on a phone, the diagram is read-only there with no
  // composer. See phase-9-chrome.md §3.
  const publicSupplierLinksSupportMobile =
    isPublicCatalogPath(location.pathname) ||
    isSupplierPublicPath(location.pathname) ||
    isAuditPath(location.pathname) ||
    isVerifierWorkspacePath(location.pathname) ||
    isUkCalculatorEmbedPath(location.pathname);

  if (isMobile && !publicSupplierLinksSupportMobile) {
    logLifecycleEvent('App', 'mobile device detected - showing unsupported message');
    return <MobileNotSupported />;
  }

  return <AppContent isSatellite={isSatellite} />;
};

export const stripePromise = loadStripe(stripePubKey);

const App = () => {
  const supportEmail = useBranding((s) => s.supportEmail);
  const locale = useLocaleStore((s) => s.locale);

  // Clerk's own localization bundle for the active locale (esES / itIT /
  // undefined for en). Loaded lazily so @clerk/localizations' 40-language
  // catalogue never enters the initial chunk, and held in state because the
  // import resolves after first paint — Clerk simply renders English until it
  // arrives, which is the correct degradation.
  const [clerkBundle, setClerkBundle] = useState(undefined);
  useEffect(() => {
    let cancelled = false;
    loadClerkLocalization(locale)
      .then((bundle) => {
        if (!cancelled) setClerkBundle(bundle);
      })
      .catch(() => {
        if (!cancelled) setClerkBundle(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const clerkLocalization = useMemo(
    () => buildClerkLocalization(supportEmail, clerkBundle),
    // `locale` is a dependency even though it is not read directly:
    // buildClerkLocalization calls i18n.t(), whose output changes with the
    // language while its arguments do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supportEmail, clerkBundle, locale],
  );

  // Log app initialization
  useEffect(() => {
    logLifecycleEvent('App', 'initialized');

    // Log environment information
    logCustomAction(`Environment: ${process.env.NODE_ENV} / ${process.env.REACT_APP_ENV_MODE}`);
    logCustomAction(`Screen: ${window.innerWidth}x${window.innerHeight}`);

    return () => {
      logLifecycleEvent('App', 'unmounted');
    };
  }, []);

  const satelliteProps = getClerkSatelliteProps();
  const isSatellite = Boolean(satelliteProps.isSatellite);

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      localization={clerkLocalization}
      {...satelliteProps}
    >
      <FreemiumProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ResponsiveAppContent isSatellite={isSatellite} />
            <CgSpotlightHost />
          </BrowserRouter>
          {/* Outside the router on purpose — the language-switch scrim must
              cover outside routes (auth, onboarding) exactly like the app. */}
          <LocaleSwitchOverlay />
        </QueryClientProvider>
      </FreemiumProvider>
    </ClerkProvider>
  );
};

export default App;
